import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import api from '../components/setupAxios';
import API_URL from "../config";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Box, Typography, TextField, Button, ButtonGroup } from "@mui/material";

const Schedule = () => {
  const { labId } = useParams();
  const [schedule, setSchedule] = useState([]);
  const [form, setForm] = useState({ dayOfWeek: "", startTime: "", endTime: "" });
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const adjustDateToDayOfWeek = (timeString, targetDay, referenceTimeString = null) => {
    const time = new Date(timeString);
    const now = new Date();

    const diff = (targetDay - now.getDay() + 7) % 7;
    let eventDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + diff,
      time.getHours(),
      time.getMinutes(),
      time.getSeconds()
    );

    if (referenceTimeString) {
      const refTime = new Date(referenceTimeString);
      const refMinutes = refTime.getHours() * 60 + refTime.getMinutes();
      const eventMinutes = eventDate.getHours() * 60 + eventDate.getMinutes();
      if (eventMinutes <= refMinutes) {
        eventDate.setDate(eventDate.getDate() + 1);
      }
    }
    return eventDate.toISOString();
  };

  useEffect(() => {
    fetchSchedules();
  }, [labId]);

  const fetchSchedules = async () => {
    try {
      const response = await api.get(`/lab-schedules`);
      const filteredSchedules = response.data.filter(s => s.lab._id === labId);
      setSchedule(filteredSchedules);
    } catch (error) {
      console.error("Error fetching schedules:", error);
    }
  };

  const formatTime = (time) => {
    if (!time) return "Invalid Time";
    const date = time instanceof Date ? time : new Date(time);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  };
  

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const createSchedule = async () => {
    if (!form.dayOfWeek || form.dayOfWeek < 0 || form.dayOfWeek > 6) {
      alert("Please enter a valid day of the week (0 for Sunday, 6 for Saturday).");
      return;
  }
  if (!form.startTime || !form.endTime) {
      alert("Please select a valid start and end time.");
      return;
  }
  if (new Date(`1970-01-01T${form.startTime}`) >= new Date(`1970-01-01T${form.endTime}`)) {
      alert("Start time must be earlier than end time.");
      return;
  }
  
    try {
      await api.post("/lab-schedules", {
        labId,
        ...form,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchSchedules();
      resetForm();
    } catch (error) {
      console.error("Error creating schedule:", error);
    }
  };

  const handleEdit = (schedule) => {
    setForm({
      dayOfWeek: schedule.dayOfWeek,
      startTime:formatTime(schedule.startTime),
      endTime: formatTime(schedule.endTime),
    });
    setEditingScheduleId(schedule._id);
  };

  const updateSchedule = async () => {
    if (!form.dayOfWeek || form.dayOfWeek < 0 || form.dayOfWeek > 6) {
      alert("Please enter a valid day of the week (0 for Sunday, 6 for Saturday).");
      return;
  }
  if (!form.startTime || !form.endTime) {
      alert("Please select a valid start and end time.");
      return;
  }
  if (new Date(`1970-01-01T${form.startTime}`) >= new Date(`1970-01-01T${form.endTime}`)) {
      alert("Start time must be earlier than end time.");
      return;
  }

    try {
      await api.put(`/lab-schedules/${editingScheduleId}`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchSchedules();
      resetForm();
    } catch (error) {
      console.error("Error updating schedule:", error);
    }
  };

  const toggleIsActive = async (scheduleId, currentStatus) => {
    try {
      await api.put(
        `/lab-schedules/${scheduleId}`,
        { isActive: !currentStatus }, 
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      fetchSchedules();
    } catch (error) {
      console.error("Error toggling isActive:", error);
    }
  };

  const deleteSchedule = async (id) => {
    if (window.confirm("Are you sure you want to delete this schedule?")) {
      try {
        await api.delete(`/lab-schedules/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        fetchSchedules();
      } catch (error) {
        console.error("Error deleting schedule:", error);
      }
    }
  };

  const resetForm = () => {
    setForm({ dayOfWeek: "", startTime: "", endTime: "" });
    setEditingScheduleId(null);
  };

  return (
    <div>
      <div className="dashboard-container">
        <div className="calendar-container">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin]}
            initialView="timeGridWeek"
            slotMinTime="08:00:00"
            slotMaxTime="23:00:00"
            events={schedule.map(s => {
              console.log("Event Data:", s.startTime, s.endTime, "DayOfWeek:", s.dayOfWeek);
              return {
                title: `${s.lab?.name || 'Unknown Lab'}`,
                start: adjustDateToDayOfWeek(s.startTime, s.dayOfWeek),
                end: adjustDateToDayOfWeek(s.endTime, s.dayOfWeek)
              };
            })}
          />
        </div>
        <div className="session-container">
          {/* Create/Edit Schedule Form above Sessions */}
          <Typography variant="h4" component="h2" sx={{ mb: 2 }}>
            {editingScheduleId ? "Edit Schedule" : "Create Schedule"}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, width: '100%', mb: 2 }}>
            <TextField
              type="number"
              name="dayOfWeek"
              placeholder="Day of Week (0-6)"
              value={form.dayOfWeek}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
            <TextField
              type="time"
              name="startTime"
              placeholder="Start Time"
              value={form.startTime}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
            <TextField
              type="time"
              name="endTime"
              placeholder="End Time"
              value={form.endTime}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
          </Box>
          {editingScheduleId ? (
            <ButtonGroup variant="contained" fullWidth sx={{ mb: 2 }}>
              <Button onClick={updateSchedule}>Update</Button>
              <Button onClick={resetForm}>Cancel</Button>
            </ButtonGroup>
          ) : (
            <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <Button variant="contained" sx={{ width: '50%' }} onClick={createSchedule}>
              Create
            </Button>
            </Box>
          )}
  
          <Typography variant="h4" component="h2" sx={{ mb: 2 }}>Sessions</Typography>
          {schedule.map((s) => (
            <div key={s._id} className="card">
              <p><strong>Day:</strong>{dayNames[s.dayOfWeek]}</p>
              <p><strong>Start Time:</strong> {formatTime(s.startTime)}</p>
              <p><strong>End Time:</strong> {formatTime(s.endTime)}</p>
              <p><strong>Status:</strong>{s.isActive ? "Active" : "Inactive"}</p>
              <ButtonGroup variant="contained" fullWidth>
                <Button onClick={() => handleEdit(s)}>Edit</Button>
                <Button onClick={() => toggleIsActive(s._id, s.isActive)}>Toggle Status</Button>
                <Button onClick={() => deleteSchedule(s._id)}>Delete</Button>
              </ButtonGroup>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
   
};

export default Schedule;
